import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { calculateCost } from "@/lib/pricing";
import { computeCodeDiff } from "@/lib/codeDiff";
import { insertFieldIntoCode, removeFieldFromCode } from "@/lib/fieldCodeSync";
import { RemotionPreview } from "@/components/preview/RemotionPreview";
import { PreviewAppearanceSection } from "@/components/preview/PreviewAppearanceSection";
import { CanvasFields } from "@/components/preview/CanvasFields";
import { SvgPreview } from "@/components/preview/SvgPreview";
import { Tabs, type Tab } from "@/components/ui/Tabs";
import { ConversationHistory } from "@/components/prompt/ConversationHistory";
import { AssistantDrawer } from "@/components/prompt/AssistantDrawer";
import { STARTER_TSX, STARTER_FIELDS } from "@/lib/starterTemplate";
import { STARTER_SVG, STARTER_SVG_FIELDS } from "@/lib/starterSvg";
import { applySvgFields } from "@/lib/svgFields";
import { embedSvgFonts } from "@/lib/svgFontEmbed";
import { SvgSettingsPanel } from "@/components/render/SvgSettingsPanel";
import { useClientsQuery } from "@/api/clients";
import { NEW_PROJECT_KIND_KEY } from "@/components/template/NewMenu";
import { IMAGE_VECTOR_KEY } from "@/components/prompt/ImageToVectorDialog";
import { FieldEditor } from "@/components/template/FieldEditor";
import { FieldSchemaEditor } from "@/components/template/FieldSchemaEditor";
import { CodeEditor } from "@/components/editor/CodeEditor";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useQueryClient } from "@tanstack/react-query";
import {
  promptKeys,
  useCheckpointMutation,
  useGenerateMutation,
  useIterateMutation,
  useSessionChainQuery,
  useSessionContextQuery,
  useSaveContextMutation,
  type ContextEntry,
} from "@/api/prompts";
import { ContextPanel } from "@/components/prompt/ContextPanel";
import { useCreateTemplateMutation } from "@/api/templates";
import { useClaudeSession, type Message } from "@/hooks/useClaudeSession";
import { apiFetch, apiUrl } from "@/api/client";
import { uploadAsset } from "@/api/assets";
import { useRouter } from "@tanstack/react-router";
import type { GenerateResponse, ProjectKind } from "@/types/prompt";
import type { FieldDefinition } from "@/types/template";

interface HistoryEntry {
  id: string;
  prompt: string;
  tsx: string;
  fields: FieldDefinition[];
}

interface PromptWorkspaceProps {
  sessionId?: string | null;
  onSessionCreated?: (rootId: string) => void;
}

type TabId = "build" | "fields" | "code" | "settings" | "context";

/** localStorage slot for the editor's uncommitted working copy. Keyed per
 *  session so drafts don't bleed between sessions; "draft" is the
 *  pre-first-save slot of a brand-new session. */
function editorDraftKey(sessionId: string | null | undefined): string {
  return `motionforge-editor-draft:${sessionId ?? "draft"}`;
}

const TEMPLATE_CATEGORIES = [
  { slug: "lower-thirds", label: "Lower Thirds" },
  { slug: "intros", label: "Intros" },
  { slug: "overlays", label: "Overlays" },
  { slug: "titles", label: "Titles" },
  { slug: "transitions", label: "Transitions" },
  { slug: "alerts", label: "Alerts" },
  { slug: "other", label: "Other" },
] as const;

/** Best-guess category from the session's prompt text — pre-selects the
 *  save dialog's category picker; the user can always override. */
function inferTemplateCategory(text: string): string {
  const t = text.toLowerCase();
  if (/lower.?third|name strap|speaker|chyron/.test(t)) return "lower-thirds";
  if (/intro|outro|stinger|logo reveal|opener|channel brand/.test(t)) return "intros";
  if (/transition|wipe|swipe|iris/.test(t)) return "transitions";
  if (/alert|notification|follower|donation|sub goal|subscriber/.test(t)) return "alerts";
  if (/overlay|hud|watermark|webcam|frame|ticker/.test(t)) return "overlays";
  if (/title|headline|kinetic type|quote card|caption card/.test(t)) return "titles";
  return "other";
}

/** Turn raw generation failures into something a human can act on.
 *  The Claude CLI reports subscription limits as
 *  "Claude AI usage limit reached|<unix-seconds>". */
function friendlyGenerationError(raw: string): string {
  const limit = raw.match(/usage limit reached\|(\d{9,13})/i);
  if (limit) {
    const ts = Number(limit[1]);
    const when = new Date(ts < 1e12 ? ts * 1000 : ts);
    return `Usage limit reached — resets at ${when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. Your prompt was put back in the prompt box.`;
  }
  if (/rate.?limit|overloaded|429/i.test(raw)) {
    return `The model is rate-limited right now — try again in a minute. Your prompt was put back in the prompt box. (${raw.slice(0, 120)})`;
  }
  return raw;
}

/** Retire the unsaved-session draft slot. Called whenever a new session
 *  becomes a real DB session (its content lives in the chain from then on)
 *  so a later New can't resurrect this session's pre-creation content. */
function clearNewSessionDraft() {
  try {
    window.localStorage.removeItem(editorDraftKey(null));
  } catch {
    // storage disabled — nothing to clear
  }
}

interface EditorDraft {
  /** Present only when the draft holds a REAL code/schema edit. A draft
   *  written for non-default prefs or field values alone omits tsx so a
   *  restore can never shadow newer committed code with a stale copy. */
  tsx?: string;
  fields?: FieldDefinition[];
  /** Current field VALUES (user's working state, not schema defaults). */
  values?: Record<string, unknown>;
  /** Keys the user changed by hand — restored into dirtyFieldsRef so the
   *  next prompt still carries them and merges still protect them. */
  dirtyKeys?: string[];
  savedAt?: number;
  /** Project kind for an unsaved new session. Missing = "remotion" (drafts
   *  written before SVG sessions existed). */
  kind?: ProjectKind;
  /** Per-project canvas natives for a Remotion session. All optional so
   *  drafts written before the Settings tab existed still parse — a missing
   *  value falls back to the default (width 1920 / height 1080 / fps 30). */
  width?: number;
  height?: number;
  fps?: number;
  /** Tenant-style handling for this project's generations. Missing =
   *  "brand" (follow the team profile). */
  styleMode?: "brand" | "client" | "off" | "custom";
  styleOverride?: string;
  /** Selected client id when styleMode === "client". */
  styleClientId?: string;
  /** Composer model choice for this project. "" / missing = default. */
  model?: string;
  /** Composer provider choice ("claude" default). */
  genProvider?: string;
}

/** Canvas defaults for a brand-new Remotion workspace session (no template
 *  row exists yet, so these live in workspace state + the editor draft). */
const CANVAS_DEFAULT_WIDTH = 1920;
const CANVAS_DEFAULT_HEIGHT = 1080;
const CANVAS_DEFAULT_FPS = 30;

/** Read the one-shot new-session kind hint set by NewMenu / SessionSidebar. */
function readNewProjectKindHint(): ProjectKind | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.sessionStorage.getItem(NEW_PROJECT_KIND_KEY);
    return v === "svg" || v === "remotion" ? v : null;
  } catch {
    return null;
  }
}

/** One-shot handoff from the Image→Vector dialog: a user instruction plus the
 *  uploaded reference image's id/filename, tagged kind=svg. The workspace seeds
 *  a NORMAL SVG starter session, attaches the image as a reference, and
 *  auto-dispatches the prompt — the generation agent owns mode/grid/colors. */
interface ImageVectorHandoff {
  prompt: string;
  assetId: string;
  filename: string;
}

function readImageVectorHandoff(): ImageVectorHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(IMAGE_VECTOR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImageVectorHandoff;
    if (
      typeof parsed?.prompt === "string" &&
      parsed.prompt.length > 0 &&
      typeof parsed?.assetId === "string" &&
      parsed.assetId.length > 0
    ) {
      return {
        prompt: parsed.prompt,
        assetId: parsed.assetId,
        filename:
          typeof parsed.filename === "string" && parsed.filename.length > 0
            ? parsed.filename
            : parsed.assetId,
      };
    }
  } catch {
    // corrupt/disabled storage — treat as no handoff
  }
  return null;
}

function readEditorDraft(key: string): EditorDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EditorDraft;
    if (parsed && typeof parsed === "object") {
      // tsx is optional (prefs-only drafts); when present it must be a
      // real string, otherwise drop it and keep the rest of the draft.
      if (typeof parsed.tsx !== "string" || parsed.tsx.length === 0) {
        delete parsed.tsx;
      }
      return parsed;
    }
  } catch {
    // corrupt/disabled storage — treat as no draft
  }
  return null;
}

import { resolveDurationFrames } from "@/lib/durationResolve";

/**
 * Merge a fresh field schema's defaults onto the prior fieldValues map:
 * keys that existed before keep the user's value; new keys get the
 * schema's default; removed keys are dropped. Without this, every
 * earlyPreview/lastResult tick clobbers anything the user edited
 * mid-generation (resolution, content_scale, color tweaks, etc.).
 */
function mergeFieldValues(
  prev: Record<string, unknown>,
  newFields: FieldDefinition[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of newFields) {
    out[f.key] = Object.prototype.hasOwnProperty.call(prev, f.key)
      ? prev[f.key]
      : (f.default as unknown);
  }
  return out;
}

/**
 * Overlay variant for mid-generation early previews: never DROP a user's
 * value just because a partial fields list doesn't mention its key yet.
 * Keeps everything from prev, adds defaults only for brand-new keys.
 */
function overlayFieldValues(
  prev: Record<string, unknown>,
  newFields: FieldDefinition[],
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...prev };
  for (const f of newFields) {
    if (!Object.prototype.hasOwnProperty.call(out, f.key)) {
      out[f.key] = f.default as unknown;
    }
  }
  return out;
}

/**
 * Three-way merge for a completed generation: decide per key between the
 * user's edited value and the model's new default.
 *
 *   - key is new                      → model's default
 *   - user edited it AND the model left the default unchanged
 *                                     → keep the user's value
 *   - the model changed the default   → adopt it (the model had the user's
 *     values in its prompt, so a changed default is an intentional design
 *     decision for THIS request) and clear the key's dirty mark
 *
 * Without the dirty set this collapses to "adopt model defaults", which is
 * also correct: values the user never touched should track the model.
 */
function mergeFieldValues3(
  prev: Record<string, unknown>,
  prevSchema: FieldDefinition[],
  newFields: FieldDefinition[],
  dirtyKeys: Set<string>,
): Record<string, unknown> {
  const prevDefaults = new Map(prevSchema.map((f) => [f.key, f.default]));
  const out: Record<string, unknown> = {};
  for (const f of newFields) {
    const hadValue = Object.prototype.hasOwnProperty.call(prev, f.key);
    const defaultUnchanged =
      prevDefaults.has(f.key) &&
      JSON.stringify(prevDefaults.get(f.key)) === JSON.stringify(f.default);
    if (hadValue && dirtyKeys.has(f.key) && defaultUnchanged) {
      out[f.key] = prev[f.key];
    } else {
      out[f.key] = f.default as unknown;
      dirtyKeys.delete(f.key);
    }
  }
  // Drop dirty marks for keys the new schema no longer has.
  for (const key of [...dirtyKeys]) {
    if (!newFields.some((f) => f.key === key)) dirtyKeys.delete(key);
  }
  return out;
}

/**
 * Build a self-contained prompt for the synthetic "edit-<templateId>"
 * entry handoff (Edit with AI / Upgrade Text Fields). Since there's no
 * real parent prompt row, the model can't be given prior TSX via the
 * usual iterate context — embed it directly in the user prompt so the
 * first turn has everything it needs to make changes.
 */
function buildSyntheticEditPrompt(
  userPrompt: string,
  tsx: string,
  fields: FieldDefinition[],
  currentValues?: Record<string, unknown>,
): string {
  // Only send values that differ from the schema defaults — those are the
  // user's deliberate customizations (e.g. a template's saved props).
  let valuesBlock = "";
  if (currentValues) {
    const changed: Record<string, unknown> = {};
    for (const f of fields) {
      if (
        Object.prototype.hasOwnProperty.call(currentValues, f.key) &&
        JSON.stringify(currentValues[f.key]) !== JSON.stringify(f.default)
      ) {
        changed[f.key] = currentValues[f.key];
      }
    }
    if (Object.keys(changed).length > 0) {
      valuesBlock = `
\`\`\`json
// current field VALUES — the user set these by hand; they are authoritative.
// Use them as the new defaults for these fields unless this request
// explicitly asks you to change one. Never revert them.
${JSON.stringify(changed, null, 2)}
\`\`\`
`;
    }
  }
  return `${userPrompt}

---
Below is the current state of the template you're editing.

\`\`\`tsx
${tsx}
\`\`\`

\`\`\`json
// field_schema
${JSON.stringify(fields, null, 2)}
\`\`\`
${valuesBlock}`;
}

export function PromptWorkspace({
  sessionId,
  onSessionCreated,
}: PromptWorkspaceProps) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeEntry, setActiveEntry] = useState<HistoryEntry | null>(null);
  const [tsxCode, setTsxCode] = useState<string>("");
  // Which kind of artifact this session produces. For an existing session it's
  // derived from the chain's root prompt; for a brand-new one it comes from the
  // NewMenu/sidebar choice (via sessionStorage hint) or a restored draft.
  //
  // The one-shot hint is captured ONCE per mount into a ref: StrictMode runs
  // mount effects twice, and if the seed effect re-read sessionStorage after
  // consuming it, the second pass would find nothing and silently reset an
  // SVG choice back to remotion.
  const newKindHintRef = useRef<ProjectKind | null | undefined>(undefined);
  if (newKindHintRef.current === undefined) {
    newKindHintRef.current = readNewProjectKindHint();
  }
  // Image→vector handoff, captured ONCE per mount for the same StrictMode
  // reason as newKindHintRef: the seed effect consumes (removes) the storage
  // key, so re-reading on the second pass would find nothing. `null` means
  // "checked, no handoff" — distinct from the `undefined` not-yet-read state.
  const imageVectorHintRef = useRef<ImageVectorHandoff | null | undefined>(
    undefined,
  );
  if (imageVectorHintRef.current === undefined) {
    imageVectorHintRef.current = readImageVectorHandoff();
  }
  const [projectKind, setProjectKind] = useState<ProjectKind>(() => {
    if (sessionId) return "remotion"; // corrected once the chain loads
    return newKindHintRef.current ?? readEditorDraft(editorDraftKey(null))?.kind ?? "remotion";
  });
  // Mirror into a ref so the [lastResult]/[earlyPreview]-only effects can read
  // the current kind without a stale closure (they intentionally omit it from
  // their dep arrays).
  const projectKindRef = useRef<ProjectKind>(projectKind);
  projectKindRef.current = projectKind;
  const isSvg = projectKind === "svg";
  // Tracks the last TSX that came from the AI (via lastResult or sessionChain).
  // Compared against tsxCode to detect user manual edits. If the user edited
  // the code in the Code tab, the difference is sent to Claude on the next
  // iterate so the model starts from the user's version, not its own last output.
  const lastAiTsxRef = useRef<string>("");
  const [moduleUrl, setModuleUrl] = useState<string | null>(null);
  const [compileError, setCompileError] = useState<string | null>(null);
  // Monotonic compile sequence: streaming triggers a compile every couple
  // of seconds on partial TSX, and responses can resolve out of order — a
  // stale partial module must never overwrite a newer good one (that's the
  // "error icon until refresh" bug), and a stale failure must not surface.
  const compileSeqRef = useRef(0);
  const [isCompiling, setIsCompiling] = useState(false);
  const [fieldSchema, setFieldSchema] = useState<FieldDefinition[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  // Keys the user changed by hand in the Fields tab. Drives (a) which values
  // ride along with the next iterate prompt so the model designs around them,
  // and (b) the 3-way merge that keeps user edits from being reverted when a
  // generation lands. Survives reloads via the editor draft.
  const dirtyFieldsRef = useRef<Set<string>>(new Set());
  // Per-project canvas natives (Remotion). A workspace session has no template
  // row, so these live here and ride the per-session editor draft. They drive
  // the preview's base dimensions + fps and are captured by Save-as-template.
  const [canvasWidth, setCanvasWidth] = useState<number>(CANVAS_DEFAULT_WIDTH);
  const [canvasHeight, setCanvasHeight] = useState<number>(CANVAS_DEFAULT_HEIGHT);
  const [canvasFps, setCanvasFps] = useState<number>(CANVAS_DEFAULT_FPS);
  // Tenant-style handling for this project's generations: follow the team
  // profile (default), turn it off, or use a custom per-project override.
  const [styleMode, setStyleMode] = useState<
    "brand" | "client" | "off" | "custom"
  >("brand");
  const [styleOverride, setStyleOverride] = useState("");
  const [styleClientId, setStyleClientId] = useState<string | null>(null);
  const { data: styleClients } = useClientsQuery();
  // Composer provider + model choice for this project. Provider is honored
  // on a session's FIRST prompt (existing sessions keep their root
  // provider); "" model = saved prefs / engine default.
  const [genProvider, setGenProvider] = useState("claude");
  const [genModel, setGenModel] = useState("");
  const [activeTab, setActiveTab] = useLocalStorage<TabId>(
    "motionforge-prompt-workspace-tab",
    "build",
  );
  const [error, setError] = useState<string | null>(null);
  const [codeFullscreen, setCodeFullscreen] = useState(false);
  const [fieldsMode, setFieldsMode] = useState<"values" | "schema">("values");
  const [fieldsFullscreen, setFieldsFullscreen] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true);
  const [pendingPromptText, setPendingPromptText] = useState<string | null>(
    null,
  );
  // Version-history dropdown (button in the workspace toolbar).
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!historyOpen) return;
    const onDown = (e: MouseEvent) => {
      if (
        historyMenuRef.current &&
        !historyMenuRef.current.contains(e.target as Node)
      ) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [historyOpen]);

  // The assistant slide-over drawer is BOTH the output window and the
  // composer — the toolbar's Prompt button opens it and prompting happens
  // in its pinned composer (the old small popover is gone).
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Reference images attached to the next generation. Uploaded as kind=reference
  // assets (server TTL cleans them up); their ids ride along on the WS
  // prompt/iterate send so the model can view them. Shared by BOTH composers
  // (the Prompt popover here and the drawer's ConversationHistory).
  const MAX_REFERENCES = 6;
  const [referenceAssets, setReferenceAssets] = useState<
    { id: string; filename: string }[]
  >([]);
  const [refUploading, setRefUploading] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);

  // One-shot auto-dispatch for the image→vector handoff. Holds the instruction
  // to send once the WS is connected; the reference chip is set alongside it in
  // the seed effect. `autoSentRef` guards against StrictMode double-send.
  const [autoSendPrompt, setAutoSendPrompt] = useState<string | null>(null);
  const autoSentRef = useRef(false);

  const attachReferenceFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setRefError(null);
      // Cap at MAX_REFERENCES total — take only as many as there's room for.
      let remaining = MAX_REFERENCES - referenceAssets.length;
      if (remaining <= 0) {
        setRefError(`You can attach up to ${MAX_REFERENCES} reference images.`);
        return;
      }
      const picked = Array.from(files).slice(0, remaining);
      setRefUploading(true);
      try {
        for (const file of picked) {
          const asset = await uploadAsset(file, "reference");
          // Videos attach their DERIVED frames (motion grid + key frames) —
          // the model reads those; the raw clip itself can't be viewed.
          const attachable = asset.derived?.length
            ? asset.derived.map((d) => ({
                id: d.id,
                filename: d.filename ?? file.name,
              }))
            : [{ id: asset.id, filename: asset.filename ?? file.name }];
          setReferenceAssets((prev) => {
            const room = MAX_REFERENCES - prev.length;
            return room <= 0 ? prev : [...prev, ...attachable.slice(0, room)];
          });
          remaining -= attachable.length;
          if (remaining <= 0) break;
        }
      } catch (err) {
        setRefError(
          err instanceof Error ? err.message : "Reference image upload failed",
        );
      } finally {
        setRefUploading(false);
      }
    },
    [referenceAssets.length],
  );

  const removeReference = useCallback((id: string) => {
    setReferenceAssets((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Portal targets: RemotionPreview renders its settings toolbar into the
  // preview-card header and its playback bar into the full-width bottom
  // strip. useState (not useRef) so the portals attach once the slots mount.
  const [previewToolbarEl, setPreviewToolbarEl] = useState<HTMLDivElement | null>(null);
  const [playbackBarEl, setPlaybackBarEl] = useState<HTMLDivElement | null>(null);

  // Editor/preview split ratio (% width of the editor pane on lg+ screens).
  // Draggable via the divider, double-click resets, persisted so the
  // workspace opens the way the user likes to work.
  const [splitPct, setSplitPct] = useLocalStorage<number>(
    "motionforge-workspace-split",
    50,
  );
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const onDividerPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const container = splitContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const onMove = (ev: PointerEvent) => {
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        setSplitPct(Math.min(75, Math.max(25, Math.round(pct * 10) / 10)));
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [setSplitPct],
  );

  // The last COMMITTED state (checkpoint save, AI result, or loaded
  // version). Editor content differing from this is "unsaved" — kept alive
  // in a per-session localStorage draft until the user hits Save.
  const baselineRef = useRef<{ tsx: string; fields: string }>({
    tsx: "",
    fields: "[]",
  });
  const checkpointMutation = useCheckpointMutation();
  const draftKey = editorDraftKey(sessionId);
  // Set when the "Edit with AI" sessionStorage handoff seeded the editor —
  // suppresses the starter-template seed on mount.
  const editHandoffRef = useRef(false);
  // Pre-fill the prompt input on first mount when arriving from a flow that
  // hands off a canned prompt (currently: TemplateDetail's Upgrade Text Fields).
  const [initialDraft, setInitialDraft] = useState<string | undefined>(undefined);

  // REST-based mutations (fallback)
  const generateMutation = useGenerateMutation();
  const iterateMutation = useIterateMutation();

  // Used to invalidate the sessions-list query after a streamed prompt
  // completes — the WebSocket flow doesn't touch React Query directly,
  // so the sidebar wouldn't otherwise know a new session exists.
  const queryClient = useQueryClient();

  const createTemplateMutation = useCreateTemplateMutation();
  const router = useRouter();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("other");

  // WebSocket streaming session
  const {
    isConnected,
    isGenerating: wsIsGenerating,
    sendCancel,
    sendInterrupt,
    sendNewSession,
    messages,
    lastResult,
    earlyPreview,
    currentLifecycle,
    currentActivity,
    generationStartedAt,
    liveOutputTokens,
    error: wsError,
    sendPrompt,
    sendIterate,
    transcriptRestoredFromCache,
    isLoadingJsonl,
    loadJsonl,
    setActiveSession,
    promoteDraftToSession,
    setMessages,
    setStyle,
    setModel,
    setProvider,
  } = useClaudeSession();

  // Keep the WS manager's outgoing style handling in sync with this
  // project's setting so every prompt/iterate carries it.
  useEffect(() => {
    setStyle(styleMode, styleOverride, styleClientId);
  }, [styleMode, styleOverride, styleClientId, setStyle]);
  useEffect(() => {
    setModel(genModel);
  }, [genModel, setModel]);
  useEffect(() => {
    setProvider(genProvider);
  }, [genProvider, setProvider]);

  // Keep the WS manager's transcript aligned with whichever session the
  // workspace is currently showing. Swaps localStorage slots on every
  // sessionId change so the Session Console reflects that session's history.
  useEffect(() => {
    setActiveSession(sessionId ?? null);
  }, [sessionId, setActiveSession]);

  // Escape exits fields fullscreen
  useEffect(() => {
    if (!fieldsFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); setFieldsFullscreen(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fieldsFullscreen]);

  useEffect(() => {
    if (!fieldsFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [fieldsFullscreen]);

  // Ctrl+. (Cmd+.) toggles fullscreen code editor from ANY tab.
  // If not on the Code tab, switches to it first. Works globally because
  // this effect lives in PromptWorkspace, which persists across tab switches.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "`") {
        e.preventDefault();
        if (!tsxCode) return; // no code to show
        setCodeFullscreen((v) => {
          if (!v) setActiveTab("code"); // switch to code tab when opening
          return !v;
        });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tsxCode, setActiveTab]);

  const isLoading = useStreaming
    ? wsIsGenerating
    : generateMutation.isPending || iterateMutation.isPending;

  // Fetch session chain when sessionId changes
  const { data: sessionChain } = useSessionChainQuery(sessionId ?? "");

  // ── Session context ledger (Context tab) ────────────────────────────
  // Server-backed once a session exists; staged in localStorage for a
  // brand-new session and sent with the first prompt (there is no root
  // row to PUT to yet). User edits are debounced into the PUT endpoint.
  const contextQuery = useSessionContextQuery(sessionId);
  const saveContextMutation = useSaveContextMutation(sessionId);
  const [stagedContext, setStagedContext] = useLocalStorage<ContextEntry[]>(
    "motionforge-context-staged",
    [],
  );
  const [contextEntries, setContextEntries] = useState<ContextEntry[]>([]);
  const contextDirtyRef = useRef(false);
  // Mirror server data into local state — but never clobber an edit that
  // hasn't been PUT yet (async-prop mirror with a dirty guard).
  useEffect(() => {
    if (sessionId && contextQuery.data && !contextDirtyRef.current) {
      setContextEntries(contextQuery.data.entries);
    }
  }, [sessionId, contextQuery.data]);
  // Debounced save of user edits for server-backed sessions.
  useEffect(() => {
    if (!sessionId || !contextDirtyRef.current) return;
    const handle = setTimeout(() => {
      contextDirtyRef.current = false;
      saveContextMutation.mutate(contextEntries);
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextEntries, sessionId]);
  const displayedContext = sessionId ? contextEntries : stagedContext;
  const handleContextChange = useCallback(
    (entries: ContextEntry[]) => {
      if (sessionId) {
        contextDirtyRef.current = true;
        setContextEntries(entries);
      } else {
        setStagedContext(entries);
      }
    },
    [sessionId, setStagedContext],
  );
  // After a generation lands, the model (or the brand extractor) may have
  // written new ledger entries — refetch so the tab shows them.
  useEffect(() => {
    if (lastResult && sessionId) {
      queryClient.invalidateQueries({
        queryKey: promptKeys.sessionContext(sessionId),
      });
    }
  }, [lastResult, sessionId, queryClient]);

  // Roll up tokens + estimated cost across every prompt in this session chain.
  // The backend records input_tokens / output_tokens / model_used on each
  // Prompt row, so there's nothing extra to track server-side. Cost is an
  // UPPER BOUND — prompt caching lowers the real bill, but we don't get the
  // cached-read breakdown back from Claude Code's stream-json output.
  const sessionUsage = useMemo(() => {
    if (!sessionChain || sessionChain.length === 0) {
      return { inputTokens: 0, outputTokens: 0, costUsd: 0 };
    }
    let inputTokens = 0;
    let outputTokens = 0;
    let costUsd = 0;
    for (const p of sessionChain) {
      const inTok = p.input_tokens ?? 0;
      const outTok = p.output_tokens ?? 0;
      inputTokens += inTok;
      outputTokens += outTok;
      costUsd += calculateCost(p.model_used, inTok, outTok);
    }
    return { inputTokens, outputTokens, costUsd };
  }, [sessionChain]);

  const compileAndPreview = useCallback(async (tsx: string) => {
    const seq = ++compileSeqRef.current;
    setIsCompiling(true);
    // Don't clear moduleUrl — keep the old preview visible while compiling.
    //
    // Preserve the user's window scroll position around the moduleUrl swap.
    // When a new module URL lands, the Player can briefly resize / the
    // workspace re-renders, and the browser's automatic scroll-anchoring
    // sometimes pulls the page to the top. Snapshot scrollY before the
    // setState, restore on the next frame after layout settles.
    const snapshotScrollY = typeof window !== "undefined" ? window.scrollY : 0;
    try {
      const result = await apiFetch<{ module_url: string }>(
        "/preview/compile",
        {
          method: "POST",
          body: JSON.stringify({ tsx_code: tsx }),
        },
      );
      // The backend returns a relative path like "/api/preview/modules/xyz.js".
      // Wrap in apiUrl() so it resolves against forge-api.hackercat.it, not
      // against the frontend origin (which would return index.html on Pages).
      if (seq !== compileSeqRef.current) return; // superseded — discard
      setModuleUrl(apiUrl(result.module_url) + `?t=${Date.now()}`);
      setCompileError(null);
      if (typeof window !== "undefined" && snapshotScrollY > 0) {
        // Two RAFs — first lets React commit the new moduleUrl, second lets
        // the Player's lazyComponent settle into layout. After that, restore.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (Math.abs(window.scrollY - snapshotScrollY) > 1) {
              window.scrollTo({ top: snapshotScrollY, behavior: "auto" });
            }
          });
        });
      }
    } catch (err) {
      if (seq !== compileSeqRef.current) return; // superseded — discard
      console.error("Compile failed:", err);
      // Preview-pane state, NOT the global generation error channel:
      // partial code fails to compile routinely mid-stream, and routing it
      // through setError consumed pendingPromptText and popped the prompt
      // open mid-generation.
      setCompileError(
        err instanceof Error ? err.message : "Failed to compile component",
      );
    } finally {
      if (seq === compileSeqRef.current) {
        setIsCompiling(false);
      }
    }
  }, []);

  // "Unsaved changes" = editor state differs from the last committed
  // version. Drives the Save button and the toolbar status dot.
  const dirty =
    tsxCode.length > 0 &&
    (tsxCode !== baselineRef.current.tsx ||
      JSON.stringify(fieldSchema) !== baselineRef.current.fields);

  // A canvas that differs from the defaults is worth persisting on its own,
  // even when the code/fields are otherwise clean — otherwise a portrait
  // project's dimensions would be lost on reload. Checkpoint Save doesn't
  // commit canvas (there's no template row), so the draft is the only home
  // for it until the user hits Save-as-template.
  const canvasNonDefault =
    canvasWidth !== CANVAS_DEFAULT_WIDTH ||
    canvasHeight !== CANVAS_DEFAULT_HEIGHT ||
    canvasFps !== CANVAS_DEFAULT_FPS ||
    styleMode !== "brand" ||
    styleOverride !== "" ||
    styleClientId !== null ||
    genModel !== "" ||
    genProvider !== "claude";

  // Keep the uncommitted working copy alive in localStorage (debounced).
  // Clicking off to another page and coming back restores it; hitting Save
  // clears it (the state is committed to the chain instead).
  useEffect(() => {
    if (!tsxCode) return;
    const handle = setTimeout(() => {
      try {
        if (dirty || canvasNonDefault || dirtyFieldsRef.current.size > 0) {
          window.localStorage.setItem(
            draftKey,
            JSON.stringify({
              // Persist code only for a REAL edit (differs from the last
              // committed version). A prefs/values-only draft that embedded
              // tsx would resurrect stale code whenever the committed
              // version changes underneath it (other device, server fix).
              ...(dirty ? { tsx: tsxCode, fields: fieldSchema } : {}),
              values: fieldValues,
              dirtyKeys: [...dirtyFieldsRef.current],
              kind: projectKind,
              width: canvasWidth,
              height: canvasHeight,
              fps: canvasFps,
              styleMode,
              styleOverride,
              styleClientId: styleClientId ?? undefined,
              model: genModel || undefined,
              genProvider: genProvider !== "claude" ? genProvider : undefined,
              savedAt: Date.now(),
            } satisfies EditorDraft),
          );
        } else {
          window.localStorage.removeItem(draftKey);
        }
      } catch {
        // storage disabled/full — drafts are best-effort
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [
    tsxCode,
    fieldSchema,
    fieldValues,
    dirty,
    draftKey,
    projectKind,
    canvasWidth,
    canvasHeight,
    canvasFps,
    canvasNonDefault,
    styleMode,
    styleOverride,
    styleClientId,
    genModel,
    genProvider,
  ]);

  // Tracks which sessionId the local `history`/`activeEntry`/`tsxCode` belong
  // to. When the user clicks a different session in the sidebar, sessionId
  // changes but sessionChain refetches asynchronously — we need to know
  // "the local state is stale, let the next sessionChain load re-hydrate it"
  // vs "this is a refetch for the same session, don't clobber in-progress
  // streamed edits". Comparing this ref to the incoming sessionId gives us
  // exactly that signal.
  const loadedSessionIdRef = useRef<string | null>(null);

  // Load session chain data when it arrives.
  //
  // Two cases this effect has to handle correctly:
  //   1. Fresh session (sessionId is null → user types → onSessionCreated
  //      sets it → sessionChain fetches → load once). Already-populated
  //      local state from the live stream must NOT be clobbered.
  //   2. Switching sessions (sessionId changes from A → B). Local state
  //      belongs to A and MUST be replaced with B's chain.
  //
  // The discriminator is `loadedSessionIdRef`: if it matches sessionId we're
  // looking at a refetch for the same session and should skip; otherwise
  // it's a switch and we reload.
  // Reset local state whenever the selected session changes — either to null
  // (user clicked "New Session") or to a different existing session (user
  // clicked a sibling in the sidebar). Without this, switching sessions
  // would leave the old session's history/code/fields visible because the
  // sessionChain-load effect skips when `loadedSessionIdRef` still matches.
  //
  // IMPORTANT: This effect MUST be declared BEFORE the sessionChain-load
  // effect below. React runs effects in declaration order, so we need to
  // clear stale state before the load effect tries to populate from the
  // (potentially cached) session chain data.
  //
  // Only the null case talks to the backend via sendNewSession() — switching
  // between existing sessions is purely a UI pivot, the Claude subprocess
  // state for the target session is handled on the next prompt via --resume.
  useEffect(() => {
    if (loadedSessionIdRef.current === (sessionId ?? null)) return;
    setHistory([]);
    setActiveEntry(null);
    setTsxCode("");
    setModuleUrl(null);
    setFieldSchema([]);
    setFieldValues({});
    // Reset canvas to defaults before the chain-load effect re-hydrates it
    // from the target session's draft (effects run in declaration order).
    setCanvasWidth(CANVAS_DEFAULT_WIDTH);
    setCanvasHeight(CANVAS_DEFAULT_HEIGHT);
    setCanvasFps(CANVAS_DEFAULT_FPS);
    setError(null);
    setShowSaveDialog(false);
    setTemplateName("");
    setActiveTab("code");

    if (sessionId === null) {
      // Explicit "New Session" — tell the backend to kill the Claude
      // subprocess and forget its claude_session_id too, otherwise the next
      // prompt would reuse the live subprocess (still holding the prior
      // conversation in memory) and give the user phantom context.
      sendNewSession();
      loadedSessionIdRef.current = null;
    }
    // For a non-null switch we deliberately leave loadedSessionIdRef alone —
    // the sessionChain-load effect will stamp it once the new chain arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Load session chain data when it arrives.
  //
  // Two cases this effect has to handle correctly:
  //   1. Fresh session (sessionId is null → user types → onSessionCreated
  //      sets it → sessionChain fetches → load once). Already-populated
  //      local state from the live stream must NOT be clobbered.
  //   2. Switching sessions (sessionId changes from A → B). Local state
  //      belongs to A and MUST be replaced with B's chain.
  //
  // The discriminator is `loadedSessionIdRef`: if it matches sessionId we're
  // looking at a refetch for the same session and should skip; otherwise
  // it's a switch and we reload.
  useEffect(() => {
    if (!sessionChain || sessionChain.length === 0) return;

    // Guard: the chain data must belong to the session we're currently
    // viewing. After a session switch, React Query may still hold stale
    // data from the previous session while the new fetch is in flight.
    const chainMatchesViewed =
      sessionId == null ||
      sessionChain.some((p) => p.id === sessionId);
    if (!chainMatchesViewed) return;

    const isSameSession = loadedSessionIdRef.current === (sessionId ?? null);
    if (isSameSession && history.length > 0 && activeEntry) return;

    const entries: HistoryEntry[] = sessionChain
      .filter((p) => p.status === "complete" && p.response_tsx)
      .map((p) => {
        let fields: FieldDefinition[] = [];
        if (p.response_fields) {
          try {
            fields = JSON.parse(p.response_fields) as FieldDefinition[];
          } catch {
            // Invalid JSON in response_fields, use empty array
          }
        }
        return {
          id: p.id,
          prompt: p.prompt_text,
          tsx: p.response_tsx!,
          fields,
        };
      });

    if (entries.length === 0) return;

    // The root prompt pins the session's kind for the whole chain.
    const rootKind: ProjectKind =
      sessionChain.find((p) => !p.parent_id)?.project_kind ??
      sessionChain[0]?.project_kind ??
      "remotion";
    setProjectKind(rootKind);

    setHistory(entries);

    const last = entries[entries.length - 1]!;
    setActiveEntry(last);
    lastAiTsxRef.current = last.tsx;
    setError(null);
    baselineRef.current = {
      tsx: last.tsx,
      fields: JSON.stringify(last.fields),
    };

    // If the user left uncommitted edits for this session, restore them ON
    // TOP of the committed version — the editor picks up where they left
    // off, with the Save button armed. Otherwise show the version as-is.
    const draft = readEditorDraft(editorDraftKey(sessionId));
    const draftHasEdits = !!draft?.tsx && draft.tsx !== last.tsx;
    const effectiveTsx = draftHasEdits ? draft!.tsx! : last.tsx;
    const effectiveFields =
      draftHasEdits && draft!.fields ? draft!.fields : last.fields;

    setTsxCode(effectiveTsx);
    setFieldSchema(effectiveFields);

    // Canvas natives live only in the per-session draft (no template row) —
    // restore them independently of whether the tsx/fields draft is in use.
    // Missing values fall back to the defaults so pre-Settings-tab drafts
    // (which never wrote canvas) parse fine.
    setCanvasWidth(draft?.width ?? CANVAS_DEFAULT_WIDTH);
    setCanvasHeight(draft?.height ?? CANVAS_DEFAULT_HEIGHT);
    setCanvasFps(draft?.fps ?? CANVAS_DEFAULT_FPS);
    setStyleMode(draft?.styleMode ?? "brand");
    setStyleOverride(draft?.styleOverride ?? "");
    setStyleClientId(draft?.styleClientId ?? null);
    setGenModel(draft?.model ?? "");
    setGenProvider(draft?.genProvider ?? "claude");

    const defaults: Record<string, unknown> = {};
    for (const field of effectiveFields) {
      defaults[field.key] = field.default;
    }
    // Re-hydrate the user's field edits from the session draft: keys the
    // user changed by hand overlay the schema defaults and their dirty
    // marks are restored, so a reload doesn't silently revert values.
    dirtyFieldsRef.current = new Set();
    if (draft?.values && draft.dirtyKeys) {
      for (const key of draft.dirtyKeys) {
        if (
          Object.prototype.hasOwnProperty.call(draft.values, key) &&
          effectiveFields.some((f) => f.key === key)
        ) {
          defaults[key] = draft.values[key];
          dirtyFieldsRef.current.add(key);
        }
      }
    }
    setFieldValues(defaults);

    // Stamp the ref so future refetches for this same session don't clobber
    // in-progress streamed edits (see guard above).
    loadedSessionIdRef.current = sessionId ?? null;

    // SVG sessions preview straight from the source (SvgPreview) — no
    // backend compile step.
    if (rootKind === "remotion") void compileAndPreview(effectiveTsx);
  }, [sessionChain, sessionId, compileAndPreview]);

  // Auto-restore transcript when switching to a session with no cached
  // messages (hard refresh, new device). Runs once per session switch
  // — separate from the chain-load effect so it doesn't re-trigger on
  // every messages change during active generation.
  const transcriptRestoredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionChain || sessionChain.length === 0) return;
    if (!sessionId) return;
    // Only run once per session switch
    if (transcriptRestoredRef.current === sessionId) return;
    // CRITICAL: never run synth while a generation is actively streaming.
    // Synth replaces the entire messages array — if it fires mid-stream,
    // it clobbers tokens that have already arrived and the user sees a
    // truncated transcript. The user wants real-time visibility for
    // mid-generation interjection; synth-during-stream actively breaks
    // that. Synth is for re-hydrating completed sessions only.
    if (wsIsGenerating) return;
    transcriptRestoredRef.current = sessionId;

    // Give the session manager a tick to load any localStorage messages
    // before deciding the transcript is empty.
    const timer = setTimeout(() => {
      // Re-check the gate inside the timer too, in case the user kicked
      // off a generation in the 100ms window.
      if (wsIsGenerating) return;
      const hasMessages = messages.length > 0;
      // "Synthesized" = the only messages we have are ones WE generated
      // from chain rows, OR a single legacy/jsonl import system blob.
      // In any of those cases we should re-synthesize from the latest
      // chain rows for a cleaner display.
      const isSynthesizedOrEmpty =
        !hasMessages ||
        messages.every(
          (m) =>
            m.role === "system" ||
            m.id === "synth-import" ||
            m.id.startsWith("synth-user-") ||
            m.id.startsWith("synth-asst-"),
        );

      if (!isSynthesizedOrEmpty) return;

      // Always prefer structured chain rows over the loadJsonl blob path —
      // chain rows give clean (user, assistant) turn boundaries instead of
      // dumping the entire JSONL as one muted system message that doesn't
      // look like history. Chain rows include the actual prompt text and
      // response_tsx, which is enough to reconstruct what the user saw.
      //
      // For PENDING turns: assistant content stays empty so live stream
      // tokens (arriving via resume_active replay) append cleanly into a
      // visibly-empty bubble — the ▎ cursor in MessageBlock provides the
      // visual "still streaming" feedback. Putting placeholder text here
      // would prefix every incoming token with "(generation still running…)"
      // and look broken.
      //
      // Race-safety: if any of the live messages already started receiving
      // streamed tokens between setActiveSession and this 100ms timer, we
      // need to preserve them. Build the synth, but for any chain row whose
      // promptId matches an EXISTING streaming message in `messages`, copy
      // that existing message's content+status into the synth. Then setMessages
      // with the merged result.
      const liveByPromptId = new Map<string, Message>();
      const liveOrphanAssistants: Message[] = [];
      for (const m of messages) {
        if (m.role !== "assistant") continue;
        if (m.promptId) {
          liveByPromptId.set(m.promptId, m);
        } else if (m.content.length > 0) {
          // Assistant body with content but no promptId. This happens when
          // tokens arrive before prompt_accepted binds an id, or when the
          // session-id was promoted (draft → real) mid-flight and the
          // remount restarted the message-id assignment race. Either way,
          // we don't want to lose the body — claim as orphan regardless
          // of status. Used to gate on status==="streaming"; that dropped
          // completed PAI replies on the floor and let the synth stub
          // them with "Generated component (X chars)".
          liveOrphanAssistants.push(m);
        }
      }
      // Chain row ids in order — orphans get attached to whichever row
      // doesn't already have a live entry, regardless of pending/complete
      // status (a completed orphan attaches to the most recent completed
      // row that has no live message).
      const orphanTargetIds = sessionChain
        .filter((p) => !liveByPromptId.has(p.id))
        .map((p) => p.id);

      const synthesized: Message[] = [];
      for (const p of sessionChain) {
        synthesized.push({
          id: `synth-user-${p.id}`,
          role: "user",
          content: p.prompt_text,
          status: "complete",
          timestamp: 0,
        });

        let live = liveByPromptId.get(p.id);
        // Orphan match: if no promptId-bound live message and this row
        // doesn't have a live entry attached yet, claim the next orphan.
        if (!live && liveOrphanAssistants.length > 0 && orphanTargetIds[0] === p.id) {
          live = liveOrphanAssistants.shift();
          orphanTargetIds.shift();
        }
        if (live && live.content.length > 0) {
          // The live stream has already started writing — preserve it,
          // binding the chain row's id so subsequent tokens route correctly.
          synthesized.push({ ...live, promptId: p.id });
          continue;
        }

        // No live content and the chain row's response_tsx alone isn't a
        // substitute for PAI's chat reply. Be honest about it: tell the
        // user the chat body wasn't preserved, and point them at the Code
        // tab for the actual artifact. Used to read "Generated component
        // (10,123 chars)" which sounded like a summary PAI emitted.
        const body = p.response_tsx
          ? "_Session restored — the original chat reply isn't persisted yet (see Code tab for the generated TSX)._"
          : p.error_message
            ? `Error: ${p.error_message}`
            : "";
        const status: Message["status"] = p.error_message
          ? "error"
          : p.status === "pending"
            ? "streaming"
            : "complete";
        synthesized.push({
          id: `synth-asst-${p.id}`,
          role: "assistant",
          content: body,
          status,
          promptId: p.id,
          errorMessage: p.error_message ?? undefined,
          timestamp: 0,
        });
      }
      if (synthesized.length > 0) setMessages(synthesized);
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionChain, sessionId]);

  // Check for "Edit with AI" context from template detail page
  useEffect(() => {
    const stored = sessionStorage.getItem("motionforge-edit-template");
    if (!stored) return;
    sessionStorage.removeItem("motionforge-edit-template");

    try {
      const ctx = JSON.parse(stored) as {
        templateId: string;
        name: string;
        tsxCode: string;
        fieldSchema: FieldDefinition[];
        defaultProps?: Record<string, unknown>;
        pendingDraft?: string;
        projectKind?: ProjectKind;
      };
      const handoffKind: ProjectKind =
        ctx.projectKind === "svg" ? "svg" : "remotion";
      setProjectKind(handoffKind);

      const entry: HistoryEntry = {
        id: `edit-${ctx.templateId}`,
        prompt: `Editing template: ${ctx.name}`,
        tsx: ctx.tsxCode,
        fields: ctx.fieldSchema,
      };

      editHandoffRef.current = true;
      baselineRef.current = {
        tsx: ctx.tsxCode,
        fields: JSON.stringify(ctx.fieldSchema),
      };
      setHistory([entry]);
      setActiveEntry(entry);
      setTsxCode(ctx.tsxCode);
      setFieldSchema(ctx.fieldSchema);

      // Seed values from the schema defaults, then overlay the template's
      // saved props — those are the user's customizations of this template
      // and must survive the AI edit. Overlaid keys are marked dirty so
      // they ride into the first prompt and the post-generation merge.
      const defaults: Record<string, unknown> = {};
      for (const field of ctx.fieldSchema) {
        defaults[field.key] = field.default;
      }
      dirtyFieldsRef.current = new Set();
      if (ctx.defaultProps) {
        for (const field of ctx.fieldSchema) {
          const saved = ctx.defaultProps[field.key];
          if (
            saved !== undefined &&
            JSON.stringify(saved) !== JSON.stringify(field.default)
          ) {
            defaults[field.key] = saved;
            dirtyFieldsRef.current.add(field.key);
          }
        }
      }
      setFieldValues(defaults);

      if (ctx.pendingDraft) {
        setInitialDraft(ctx.pendingDraft);
      }

      // SVG previews straight from source — only Remotion compiles.
      if (handoffKind === "remotion") {
        void compileAndPreview(ctx.tsxCode);
      }
    } catch {
      // Invalid stored context, ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed a brand-new session so the workspace is immediately usable:
  // restore an uncommitted draft if one exists for the new-session slot,
  // otherwise open the starter component. The user can hand-edit, wire
  // fields, or prompt — in any order. Existing sessions are hydrated by
  // the chain-load effect instead, and the "Edit with AI" handoff above
  // takes precedence when present.
  useEffect(() => {
    if (sessionId) return;
    if (editHandoffRef.current) return;

    const draft = readEditorDraft(editorDraftKey(null));
    // Explicit choice from NewMenu/sidebar wins; else the draft's own kind;
    // else remotion. The hint was captured once at mount (StrictMode-safe);
    // clear the storage slot so it doesn't leak to the next new session —
    // removal is idempotent, so the double-run is harmless.
    const hint = newKindHintRef.current;
    // The handoff was captured into imageVectorHintRef during render; clear the
    // storage slot here so it doesn't leak to the next new session (removal is
    // idempotent, so StrictMode's double-run is harmless).
    const imageVector = imageVectorHintRef.current;
    try {
      window.sessionStorage.removeItem(NEW_PROJECT_KIND_KEY);
      window.sessionStorage.removeItem(IMAGE_VECTOR_KEY);
    } catch {
      // ignore
    }
    const kind: ProjectKind = hint ?? draft?.kind ?? "remotion";
    setProjectKind(kind);

    // An EXPLICIT New (kind hint present) always opens that kind's starter —
    // resurrecting a leftover draft here reads as "the New button did
    // nothing". Drafts only restore on organic mounts (refresh, navigating
    // back to /create) where no hint exists.
    const draftUsable =
      !hint && !!draft?.tsx && (draft.kind ?? "remotion") === kind;
    if (hint) {
      try {
        window.localStorage.removeItem(editorDraftKey(null));
      } catch {
        // ignore
      }
    }

    if (kind === "svg") {
      const tsx = draftUsable ? draft!.tsx! : STARTER_SVG;
      const fields =
        draftUsable && draft!.fields ? draft!.fields : STARTER_SVG_FIELDS;
      // Baseline is always the starter so a restored draft reads as unsaved.
      baselineRef.current = {
        tsx: STARTER_SVG,
        fields: JSON.stringify(STARTER_SVG_FIELDS),
      };
      setTsxCode(tsx);
      setFieldSchema(fields);
      const defaults: Record<string, unknown> = {};
      for (const field of fields) defaults[field.key] = field.default;
      setFieldValues(defaults);

      // Image→vector handoff: this is a NORMAL svg starter session (seeded
      // above). Attach the uploaded image as a reference chip and queue the
      // instruction for a one-shot auto-dispatch through the normal composer
      // send path — the generation agent's vectorize tool owns mode/grid/
      // colors. The chip must be set here (not in the auto-send effect) so it's
      // already in handleGenerate's closure and rides the WS message.
      if (imageVector) {
        setReferenceAssets([
          { id: imageVector.assetId, filename: imageVector.filename },
        ]);
        setAutoSendPrompt(imageVector.prompt);
      }
      // No compile — SvgPreview renders the substituted source directly.
      return;
    }

    const tsx = draftUsable ? draft!.tsx! : STARTER_TSX;
    const fields = draftUsable && draft!.fields ? draft!.fields : STARTER_FIELDS;

    // Baseline is always the starter: a restored draft should read as
    // "unsaved changes" so Save is armed.
    baselineRef.current = {
      tsx: STARTER_TSX,
      fields: JSON.stringify(STARTER_FIELDS),
    };

    setTsxCode(tsx);
    setFieldSchema(fields);
    // Restore this project's canvas from a usable draft; a fresh starter keeps
    // the defaults the state already holds.
    if (draftUsable) {
      setCanvasWidth(draft!.width ?? CANVAS_DEFAULT_WIDTH);
      setCanvasHeight(draft!.height ?? CANVAS_DEFAULT_HEIGHT);
      setCanvasFps(draft!.fps ?? CANVAS_DEFAULT_FPS);
    }
    const defaults: Record<string, unknown> = {};
    for (const field of fields) {
      defaults[field.key] = field.default;
    }
    setFieldValues(defaults);
    void compileAndPreview(tsx);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle early preview: Claude wrote component.tsx to disk during
  // generation. Compile and show the preview immediately — don't wait
  // for the full stream_end. Only updates the preview; does NOT add
  // to history, promote draft, or finalize the prompt.
  useEffect(() => {
    if (!earlyPreview || !earlyPreview.tsx) return;
    setTsxCode(earlyPreview.tsx);
    lastAiTsxRef.current = earlyPreview.tsx;
    if (earlyPreview.fields.length > 0) {
      setFieldSchema(earlyPreview.fields);
      // Merge: keep user-edited values for keys still present.
      // Overlay, don't merge: early-preview field lists can be partial (or
      // empty) while Claude is still writing — a strict merge here silently
      // dropped every user-edited value, which is why edits used to revert
      // after the final result landed on an empty prev map.
      if (earlyPreview.fields.length > 0) {
        setFieldValues((prev) => overlayFieldValues(prev, earlyPreview.fields));
      }
    }
    // SVG sessions preview from source; only Remotion needs a compile.
    if (projectKindRef.current === "remotion") {
      void compileAndPreview(earlyPreview.tsx);
    }
  }, [earlyPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle WebSocket streaming result (final)
  useEffect(() => {
    if (!lastResult || !lastResult.tsx) return;

    // pendingPromptText is null when this effect fires on a fresh
    // PromptWorkspace mount (e.g. session-id promotion remounts via the
    // `key` prop in Create.tsx, but the WS manager keeps lastResult set).
    // In that case the chain query will provide the real prompt_text once
    // it refetches — DON'T fabricate a "streamed generation" placeholder
    // that ends up duplicated next to the synth-loaded entry.
    const previewAlreadyMatches = lastResult.tsx === lastAiTsxRef.current;
    let entry: HistoryEntry | null = null;

    if (pendingPromptText) {
      entry = {
        id: lastResult.promptId ?? crypto.randomUUID(),
        prompt: pendingPromptText,
        tsx: lastResult.tsx,
        fields: lastResult.fields,
      };
      setHistory((prev) => [...prev, entry!]);
      setActiveEntry(entry);
    } else if (lastResult.promptId) {
      // Promote activeEntry pointer so preview/code track the new TSX,
      // but skip the local-history push — the chain refetch below will
      // bring in the row with its correct prompt_text.
      setActiveEntry({
        id: lastResult.promptId,
        prompt: "",
        tsx: lastResult.tsx,
        fields: lastResult.fields,
      });
    }

    if (!previewAlreadyMatches) {
      setTsxCode(lastResult.tsx);
      lastAiTsxRef.current = lastResult.tsx;
    }
    setFieldSchema(lastResult.fields);
    setError(null);
    setPendingPromptText(null);
    // The AI result is a committed version (its prompt row is persisted) —
    // it becomes the new baseline and supersedes any local draft.
    baselineRef.current = {
      tsx: lastResult.tsx,
      fields: JSON.stringify(lastResult.fields),
    };
    try {
      window.localStorage.removeItem(draftKey);
    } catch {
      // ignore
    }

    // Three-way merge: keys the user edited keep their value unless the
    // model deliberately changed that field's default this turn (it had the
    // user's values in its prompt, so a changed default is an intentional
    // response to the request). Everything else tracks the new schema.
    // fieldSchema still holds the PREVIOUS schema here — setFieldSchema
    // above hasn't re-rendered yet.
    setFieldValues((prev) =>
      mergeFieldValues3(prev, fieldSchema, lastResult.fields, dirtyFieldsRef.current),
    );

    if (!previewAlreadyMatches && projectKindRef.current === "remotion") {
      void compileAndPreview(lastResult.tsx);
    }

    if (history.length === 0 && lastResult.promptId) {
      const pid = lastResult.promptId;
      // Pre-stamp the loaded-session ref BEFORE telling the parent about the
      // new sessionId. Otherwise the parent sets sessionId=pid, the reset
      // effect sees it differs from loadedSessionIdRef (still null), and
      // wipes the entry we literally just set from the live stream. With
      // the ref pre-stamped, the reset effect correctly sees "same session"
      // and no-ops.
      loadedSessionIdRef.current = pid;
      // Migrate the WS manager's draft transcript slot over to the real
      // session id so subsequent setActiveSession(pid) calls load our
      // just-completed turn instead of an empty string.
      promoteDraftToSession(pid);
      clearNewSessionDraft();
      setTimeout(() => onSessionCreated?.(pid), 100);
    }

    // Refetch the sidebar's sessions list AND the current session's chain.
    // Why both: the chain feeds the synth path that rebuilds Session
    // History with real prompt_text from the DB, replacing any local
    // entries pushed via setHistory above.
    //
    // Why retry after a delay: the persist watcher writes the new prompt
    // row asynchronously. Stream_end can land before the row is committed.
    // Refetching only once would race and miss the row → sidebar stale.
    const refetchPromptQueries = () => {
      void queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey;
          // Match the sessions LIST: ["prompts", "sessions", {params}]
          if (
            key[0] === "prompts" &&
            key[1] === "sessions" &&
            (key.length === 2 || typeof key[2] === "object")
          ) {
            return true;
          }
          // Match the current session's CHAIN:
          // ["prompts", "sessions", "<rootId>"]
          const rootId =
            sessionId ?? (lastResult?.promptId ?? null);
          if (
            rootId &&
            key[0] === "prompts" &&
            key[1] === "sessions" &&
            key[2] === rootId
          ) {
            return true;
          }
          return false;
        },
      });
    };
    refetchPromptQueries();
    // Catch the persist-watcher write race: row is typically committed
    // within a few hundred ms of stream_end. 1200ms is a forgiving second
    // pass; if it still misses, the user is on a very slow disk and the
    // page-refresh fallback still works.
    const retryHandle = setTimeout(refetchPromptQueries, 1200);
    return () => clearTimeout(retryHandle);
  }, [lastResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Forward WebSocket errors
  useEffect(() => {
    if (wsError && useStreaming) {
      setError(wsError);
    }
  }, [wsError, useStreaming]);

  // A failed generation must not eat the prompt: restore the text into the
  // drawer's composer and open the drawer so the restoration is visible.
  useEffect(() => {
    if (!error || !pendingPromptText) return;
    const failedText = pendingPromptText;
    setPendingPromptText(null);
    setInitialDraft(failedText);
    setDrawerOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Auto-switch tab when fields become empty/available
  useEffect(() => {
    if (activeTab === "fields" && fieldSchema.length === 0 && tsxCode) {
      setActiveTab("code");
    }
  }, [fieldSchema.length, tsxCode, activeTab, setActiveTab]);

  // Migrate the old persisted "render" tab id (renamed "Settings") in storage
  // so the dead value doesn't linger. shownTab already maps it for rendering;
  // this rewrites it so it's normalized permanently.
  useEffect(() => {
    if ((activeTab as string) === "render") setActiveTab("settings");
  }, [activeTab, setActiveTab]);

  const applyResponse = useCallback(
    (promptText: string, data: GenerateResponse) => {
      const entry: HistoryEntry = {
        id: data.prompt_id,
        prompt: promptText,
        tsx: data.tsx,
        fields: data.fields,
      };
      setHistory((prev) => [...prev, entry]);
      setActiveEntry(entry);
      setTsxCode(data.tsx);
      setFieldSchema(data.fields);
      setError(null);
      baselineRef.current = {
        tsx: data.tsx,
        fields: JSON.stringify(data.fields),
      };

      const defaults: Record<string, unknown> = {};
      for (const field of data.fields) {
        defaults[field.key] = field.default;
      }
      setFieldValues(defaults);

      // SVG previews from source; only Remotion needs a compile.
      if (!isSvg) void compileAndPreview(data.tsx);
    },
    [compileAndPreview, isSvg],
  );

  const handleGenerate = useCallback(
    (promptText: string) => {
      setError(null);
      setPendingPromptText(promptText);

      // Synthetic activeEntry from "Edit with AI" / "Upgrade Text Fields" —
      // the id is "edit-<templateId>", a placeholder, never inserted as a
      // prompt row, so it can't be passed as parent_id (would FK-fail at
      // INSERT). Treat the first send as a NEW session and embed the
      // current TSX + field schema in the prompt so Claude has the context
      // it would have gotten from a real parent row's response_tsx. After
      // the response lands, activeEntry is replaced with the real prompt_id
      // and subsequent sends iterate normally.
      const isSyntheticEdit = activeEntry?.id?.startsWith("edit-") === true;
      const effectivePrompt = isSyntheticEdit
        ? buildSyntheticEditPrompt(promptText, tsxCode, fieldSchema, fieldValues)
        : promptText;
      const useIteratePath = !!activeEntry && !isSyntheticEdit;

      // Values the user changed by hand (restricted to keys still in the
      // schema). These ride along with iterate prompts so the model knows
      // the current state, designs around it, and adopts these values as
      // the new defaults instead of reverting them.
      const dirtyValues: Record<string, unknown> = {};
      for (const key of dirtyFieldsRef.current) {
        if (
          fieldSchema.some((f) => f.key === key) &&
          Object.prototype.hasOwnProperty.call(fieldValues, key)
        ) {
          dirtyValues[key] = fieldValues[key];
        }
      }
      const dirtyArg =
        Object.keys(dirtyValues).length > 0 ? dirtyValues : undefined;

      if (useStreaming && isConnected) {
        const referenceIds = referenceAssets.map((a) => a.id);
        const refArg = referenceIds.length > 0 ? referenceIds : undefined;
        // Attached files land in the context ledger too — the tab is the
        // session's durable record of everything the generations draw on.
        const fileEntries: ContextEntry[] = referenceAssets.map((a) => ({
          key: `file.${a.filename}`,
          type: "file",
          value: a.filename,
          source: "user",
        }));
        if (fileEntries.length > 0 && sessionId) {
          const merged = [
            ...contextEntries.filter(
              (e) => !fileEntries.some((f) => f.key === e.key),
            ),
            ...fileEntries,
          ];
          contextDirtyRef.current = true;
          setContextEntries(merged);
        }
        if (useIteratePath) {
          // Detect user manual edits and send only the diff to save tokens.
          // computeCodeDiff returns null if identical, "DIFF:..." for small
          // changes (~80-95% token savings), or "FULL:..." if the user
          // rewrote most of the file.
          const diffPayload = computeCodeDiff(lastAiTsxRef.current, tsxCode);
          sendIterate(
            promptText,
            activeEntry!.id,
            diffPayload ?? undefined,
            refArg,
            dirtyArg,
          );
        } else {
          // New session: tell the backend which skill to invoke. Honored only
          // on this first prompt; children inherit the root's kind. Context
          // entries staged in the Context tab seed the new session's ledger.
          const staged = [
            ...stagedContext.filter(
              (e) => !fileEntries.some((f) => f.key === e.key),
            ),
            ...fileEntries,
          ];
          sendPrompt(
            effectivePrompt,
            projectKind,
            refArg,
            staged.length > 0 ? staged : undefined,
          );
          if (stagedContext.length > 0) setStagedContext([]);
        }
        // Chips are consumed by the send — clear them so the next turn starts
        // fresh (server TTL handles the uploaded asset cleanup).
        if (referenceIds.length > 0) setReferenceAssets([]);
      } else {
        if (useIteratePath) {
          iterateMutation.mutate(
            { prompt: promptText, parent_id: activeEntry!.id },
            {
              onSuccess: (data) => applyResponse(promptText, data),
              onError: (err) => setError(err.message),
            },
          );
        } else {
          generateMutation.mutate(
            { prompt: effectivePrompt },
            {
              onSuccess: (data) => {
                applyResponse(promptText, data);
                clearNewSessionDraft();
                onSessionCreated?.(data.prompt_id);
              },
              onError: (err) => setError(err.message),
            },
          );
        }
      }
    },
    [
      useStreaming,
      isConnected,
      activeEntry,
      sendPrompt,
      sendIterate,
      generateMutation,
      iterateMutation,
      applyResponse,
      onSessionCreated,
      projectKind,
      referenceAssets,
      fieldValues,
      fieldSchema,
      tsxCode,
      sessionId,
      contextEntries,
      stagedContext,
      setStagedContext,
    ],
  );

  // Fire the image→vector handoff's instruction exactly once, through the same
  // composer send path a manual prompt uses (handleGenerate). Waits for the WS
  // to connect so the streaming branch is taken and the reference id rides the
  // WS message — sending while disconnected would fall back to the REST path,
  // which doesn't thread references. The chip was set alongside autoSendPrompt
  // in the seed effect, so it's already in handleGenerate's closure.
  useEffect(() => {
    if (!autoSendPrompt || autoSentRef.current) return;
    if (!useStreaming || !isConnected) return;
    autoSentRef.current = true;
    handleGenerate(autoSendPrompt);
    setAutoSendPrompt(null);
  }, [autoSendPrompt, useStreaming, isConnected, handleGenerate]);

  const handleHistorySelect = useCallback(
    (entry: HistoryEntry) => {
      setActiveEntry(entry);
      setTsxCode(entry.tsx);
      setFieldSchema(entry.fields);
      baselineRef.current = {
        tsx: entry.tsx,
        fields: JSON.stringify(entry.fields),
      };
      const defaults: Record<string, unknown> = {};
      for (const field of entry.fields) {
        defaults[field.key] = field.default;
      }
      setFieldValues(defaults);
      // SVG previews from source; only Remotion needs a compile.
      if (!isSvg) void compileAndPreview(entry.tsx);
    },
    [compileAndPreview, isSvg],
  );

  // Manual save — commit the current editor state as a version in the
  // session chain. First save of a new session creates the session itself.
  const handleCheckpoint = useCallback(() => {
    if (!tsxCode || checkpointMutation.isPending) return;
    const parentId =
      activeEntry && !activeEntry.id.startsWith("edit-")
        ? activeEntry.id
        : null;
    // Persist the real field schema for both kinds — SVG fields drive the
    // `{{key}}` substitution. project_kind tags a new root so it's typed (and
    // validated) as SVG on the backend.
    //
    // Fold the CURRENT field values in as the saved schema's defaults: a
    // manual save is "lock in what I'm looking at", and the checkpoint's
    // onSuccess clears the localStorage draft — without this fold the
    // user's working values have no durable copy and silently revert to
    // generation defaults on the next reload.
    const savedFields = fieldSchema.map((f) =>
      Object.prototype.hasOwnProperty.call(fieldValues, f.key)
        ? { ...f, default: fieldValues[f.key] as FieldDefinition["default"] }
        : f,
    );
    checkpointMutation.mutate(
      {
        tsx: tsxCode,
        fields: savedFields,
        parent_id: parentId,
        project_kind: projectKind,
      },
      {
        onSuccess: (res) => {
          const entry: HistoryEntry = {
            id: res.prompt_id,
            prompt: "Manual save",
            tsx: tsxCode,
            fields: savedFields,
          };
          setHistory((prev) => [...prev, entry]);
          setActiveEntry(entry);
          baselineRef.current = {
            tsx: tsxCode,
            fields: JSON.stringify(savedFields),
          };
          try {
            window.localStorage.removeItem(draftKey);
          } catch {
            // ignore
          }
          setError(null);
          if (!sessionId) {
            // This save founded the session — adopt the new root id.
            loadedSessionIdRef.current = res.session_id;
            clearNewSessionDraft();
            onSessionCreated?.(res.session_id);
          }
        },
        onError: (err) => setError(err.message),
      },
    );
  }, [
    tsxCode,
    fieldSchema,
    fieldValues,
    activeEntry,
    checkpointMutation,
    sessionId,
    onSessionCreated,
    draftKey,
    isSvg,
    projectKind,
  ]);

  // Filename stem for an SVG download — derived from the session's root
  // prompt, slugified. Falls back to "graphic".
  const svgFileBase = useMemo(() => {
    const raw = history[0]?.prompt?.trim();
    if (!raw) return "graphic";
    const slug = raw
      .slice(0, 40)
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    return slug || "graphic";
  }, [history]);

  // SVG source with the current field values substituted in — what the
  // preview shows and what Download / the saved template render. Only
  // computed for SVG sessions (Remotion TSX has no tokens to substitute).
  const substitutedSvg = useMemo(
    () => (isSvg ? applySvgFields(tsxCode, fieldValues) : ""),
    [isSvg, tsxCode, fieldValues],
  );

  // Download the substituted SVG (current field values) as a .svg file, with
  // referenced fonts inlined so the file renders correctly outside the app.
  const handleDownloadSvg = useCallback(async () => {
    if (!substitutedSvg) return;
    const embedded = await embedSvgFonts(substitutedSvg);
    const url = URL.createObjectURL(
      new Blob([embedded], { type: "image/svg+xml" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${svgFileBase}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [substitutedSvg, svgFileBase]);

  // Throw away uncommitted edits and restore the last committed version.
  const handleDiscard = useCallback(() => {
    const base = baselineRef.current;
    if (!base.tsx) return;
    let fields: FieldDefinition[] = [];
    try {
      fields = JSON.parse(base.fields) as FieldDefinition[];
    } catch {
      // keep empty
    }
    setTsxCode(base.tsx);
    setFieldSchema(fields);
    setFieldValues((prev) => mergeFieldValues(prev, fields));
    try {
      window.localStorage.removeItem(draftKey);
    } catch {
      // ignore
    }
    // SVG previews from source; only Remotion needs a compile.
    if (!isSvg) void compileAndPreview(base.tsx);
  }, [draftKey, compileAndPreview, isSvg]);

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      dirtyFieldsRef.current.add(key);
      setFieldValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const saveTemplate = useCallback(() => {
    if (!templateName.trim()) return;
    // Clamp canvas to the backend's accepted ranges (width/height 16..8192,
    // fps 1..120) so a blank/oversized input doesn't get rejected. An fps
    // field value still wins over the project canvas fps, matching the preview.
    const canvasFpsSafe = Math.max(
      1,
      Math.min(120, Math.round(canvasFps) || CANVAS_DEFAULT_FPS),
    );
    const fps = typeof fieldValues.fps === "number" && fieldValues.fps > 0 ? fieldValues.fps : canvasFpsSafe;
    createTemplateMutation.mutate(
      {
        name: templateName.trim(),
        description: activeEntry?.prompt ?? "",
        category: templateCategory,
        tsx_code: tsxCode,
        field_schema: fieldSchema,
        default_props: fieldValues,
        prompt_id: activeEntry?.id,
        duration_frames: resolveDurationFrames(fieldValues, fps, 150, fieldSchema),
        fps,
        // Per-project canvas natives captured from the Settings tab so the
        // saved template opens at the size the user designed against.
        width: Math.max(
          16,
          Math.min(8192, Math.round(canvasWidth) || CANVAS_DEFAULT_WIDTH),
        ),
        height: Math.max(
          16,
          Math.min(8192, Math.round(canvasHeight) || CANVAS_DEFAULT_HEIGHT),
        ),
        // Tags the created root so the backend validates it as SVG and skips
        // the font / no-plain-text-field checks.
        project_kind: projectKind,
      },
      {
        onSuccess: (saved) => {
          setShowSaveDialog(false);
          setTemplateName("");
          void router.navigate({
            to: "/templates/$id",
            params: { id: saved.id },
          });
        },
      },
    );
  }, [
    templateName,
    templateCategory,
    createTemplateMutation,
    activeEntry,
    tsxCode,
    fieldSchema,
    fieldValues,
    router,
    isSvg,
    projectKind,
    canvasWidth,
    canvasHeight,
    canvasFps,
  ]);

  // Both kinds get Code + Fields + Settings. SVG fields drive `{{key}}`
  // substitution; the SVG Settings tab is the viewBox readout + PNG/SVG export
  // panel, while the Remotion Settings tab holds the per-project canvas
  // (width/height/fps) + preview appearance. Neither has a render pipeline in
  // the workspace, so there's no render-output section here.
  const tabs: Tab[] = [
    { id: "code", label: "Code", disabled: !tsxCode },
    {
      id: "fields",
      label: "Fields",
      badge: fieldSchema.length > 0 ? fieldSchema.length : undefined,
      disabled: fieldSchema.length === 0,
    },
    // Context is never disabled — staging brand colors/fonts BEFORE the
    // first generation is exactly its point.
    {
      id: "context",
      label: "Context",
      badge: displayedContext.length > 0 ? displayedContext.length : undefined,
    },
    { id: "settings", label: "Settings", disabled: !tsxCode },
  ];
  // The workspace is editor-first: the old "build" chat tab moved into the
  // assistant drawer. Normalize any persisted "build" selection to Code, and
  // the old "render" tab id (renamed "Settings") to "settings" so a returning
  // user never lands on a dead tab.
  const shownTab: TabId =
    activeTab === "build"
      ? "code"
      : (activeTab as string) === "render"
        ? "settings"
        : activeTab;

  // Clamp the canvas natives to sane bounds before handing them to the Player.
  // A blank input parses to 0, which would break the composition — fall back
  // to the default in that case (the Settings input still shows what's typed).
  const previewCanvasWidth = Math.max(
    16,
    Math.min(8192, canvasWidth || CANVAS_DEFAULT_WIDTH),
  );
  const previewCanvasHeight = Math.max(
    16,
    Math.min(8192, canvasHeight || CANVAS_DEFAULT_HEIGHT),
  );

  // Save-as-template control (name input dialog + button). Shared by both the
  // Remotion and SVG preview headers so the create-template flow — including
  // its inline error surface — stays identical across kinds.
  const saveAsTemplateControl = showSaveDialog ? (
    <div className="relative flex shrink-0 items-center gap-1.5">
      <input
        type="text"
        value={templateName}
        onChange={(e) => setTemplateName(e.target.value)}
        placeholder="Template name..."
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && templateName.trim()) {
            saveTemplate();
          }
          if (e.key === "Escape") setShowSaveDialog(false);
        }}
        className="w-40 rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
      />
      <select
        value={templateCategory}
        onChange={(e) => setTemplateCategory(e.target.value)}
        aria-label="Template category"
        className="rounded-lg border border-line bg-surface px-1.5 py-1 text-xs text-ink focus:border-line-strong focus:outline-none"
      >
        {TEMPLATE_CATEGORIES.map((c) => (
          <option key={c.slug} value={c.slug}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        onClick={saveTemplate}
        disabled={!templateName.trim() || createTemplateMutation.isPending}
        className="rounded-lg bg-accent px-2.5 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
      >
        {createTemplateMutation.isPending ? "Saving..." : "Save"}
      </button>
      <button
        onClick={() => setShowSaveDialog(false)}
        className="text-xs text-ink-2 hover:text-ink"
      >
        Cancel
      </button>
      {/* Backend rejections (invalid SVG, font not registered, plain text
          fields, …) surface here — a silent no-op reads as "the button is
          broken". */}
      {createTemplateMutation.isError && (
        <div className="absolute right-0 top-full z-40 mt-2 w-72 rounded-lg border border-danger/30 bg-surface px-3 py-2 text-xs text-danger shadow-lg">
          {createTemplateMutation.error instanceof Error
            ? createTemplateMutation.error.message
            : "Save failed"}
        </div>
      )}
    </div>
  ) : (
    <button
      onClick={() => {
        createTemplateMutation.reset();
        setTemplateCategory(
          inferTemplateCategory(activeEntry?.prompt ?? history[0]?.prompt ?? ""),
        );
        setShowSaveDialog(true);
      }}
      disabled={!tsxCode}
      className="shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink disabled:opacity-40"
    >
      Save as template
    </button>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Main area: split view — editor/fields on the left, live preview on
          the right. Each pane scrolls internally; the page doesn't. While a
          generation runs, a floating pill overlays the top center. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {error && !isLoading && (
          <div className="absolute left-1/2 top-2 z-30 flex max-w-[560px] -translate-x-1/2 items-start gap-2 rounded-xl border border-danger/40 bg-surface py-2 pl-3 pr-2 text-[13px] text-danger shadow-lg">
            <span className="min-w-0 flex-1">{friendlyGenerationError(error)}</span>
            <button
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              className="shrink-0 rounded p-0.5 text-ink-3 transition-colors hover:text-ink"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {isLoading && (
          <div className="absolute left-1/2 top-2 z-30 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-line bg-surface py-1.5 pl-3 pr-2 text-[13px] text-ink-2 shadow-lg">
            <svg className="h-3.5 w-3.5 shrink-0 animate-spin text-accent" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span>Applying your changes…</span>
            <button
              onClick={() => setDrawerOpen(true)}
              className="font-medium text-accent hover:underline"
            >
              View output
            </button>
            <button
              onClick={sendCancel}
              className="rounded-full px-2 py-0.5 font-medium text-danger transition-colors hover:bg-danger-soft"
            >
              Stop
            </button>
          </div>
        )}
        <div
          style={{ "--split": `${splitPct}%` } as React.CSSProperties}
          className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-2"
        >
          {/* Unified header bar: tabs + editor controls left of the split
              line, preview title/toolbar/save-as-template on the right. */}
          <div className="flex shrink-0 items-stretch border-b border-line">
            <div className="flex min-w-0 flex-1 items-center lg:w-[var(--split)] lg:flex-none">
              <Tabs
                className="w-full !border-b-0"
                tabs={tabs}
                activeId={shownTab}
                onChange={(id) => setActiveTab(id as TabId)}
                rightSlot={
                  <div className="flex items-center gap-1.5 py-1">
                    {dirty && (
                      <>
                        <button
                          onClick={handleCheckpoint}
                          disabled={checkpointMutation.isPending}
                          className="rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
                        >
                          {checkpointMutation.isPending ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={handleDiscard}
                          className="px-1 text-xs text-ink-2 hover:text-ink"
                        >
                          Discard
                        </button>
                      </>
                    )}
                    {history.length > 0 && (
                      <div className="relative" ref={historyMenuRef}>
                        <button
                          type="button"
                          onClick={() => setHistoryOpen((v) => !v)}
                          className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                        >
                          History
                          <span className="rounded bg-surface-3 px-1 text-[10px] text-ink-3">
                            {history.length}
                          </span>
                        </button>
                        {historyOpen && (
                          <div className="absolute right-0 top-full z-40 mt-1.5 max-h-80 w-80 overflow-y-auto rounded-xl border border-line bg-surface p-1.5 shadow-lg">
                            {history.map((entry, i) => (
                              <button
                                key={entry.id}
                                onClick={() => {
                                  handleHistorySelect(entry);
                                  setHistoryOpen(false);
                                }}
                                className={`flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                                  activeEntry?.id === entry.id
                                    ? "bg-accent-soft text-accent"
                                    : "text-ink-2 hover:bg-surface-3 hover:text-ink"
                                }`}
                              >
                                <span className="mt-0.5 shrink-0 text-xs text-ink-3">
                                  v{i + 1}
                                </span>
                                <span className="line-clamp-2 flex-1">{entry.prompt}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(true)}
                      className="flex items-center gap-1 rounded-lg bg-accent px-2.5 py-1 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover"
                    >
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9L12 2zm7 12l.95 3.05L23 18l-3.05.95L19 22l-.95-3.05L15 18l3.05-.95L19 14z" />
                      </svg>
                      Prompt
                    </button>
                  </div>
                }
              />
            </div>
            <div className="hidden w-[5px] shrink-0 lg:block" />
            <div className="hidden min-w-0 flex-1 items-center gap-3 px-3 lg:flex">
              <span className="shrink-0 text-sm font-medium text-ink-2">
                {isSvg ? "Preview" : "Live preview"}
              </span>
              {isSvg ? (
                // SVG mode: no player toolbar portal. Download (substituted
                // SVG) + Save-as-template aligned to the right.
                <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
                  <button
                    onClick={handleDownloadSvg}
                    disabled={!tsxCode}
                    className="shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink disabled:opacity-40"
                  >
                    Download
                  </button>
                  {saveAsTemplateControl}
                </div>
              ) : (
                <>
              {/* RemotionPreview portals its zoom/BG/scale/resolution
                  toolbar here. Empty until a module is loaded. */}
              <div
                ref={setPreviewToolbarEl}
                className="flex min-w-0 flex-1 items-center justify-end"
              />
              {/* Publishing lives with the preview — far from the code
                  Save so the two can't be confused. */}
              {saveAsTemplateControl}
                </>
              )}
            </div>
          </div>
          {/* Body: the two panes, touching, split by the drag line. */}
          <div
            ref={splitContainerRef}
            className="flex min-h-0 flex-1 flex-col lg:flex-row"
          >
          {/* Drag line — desktop only. Double-click resets to 50/50. */}
          <div
            role="separator"
            aria-orientation="vertical"
            title="Drag to resize · double-click to reset"
            onPointerDown={onDividerPointerDown}
            onDoubleClick={() => setSplitPct(50)}
            className="group relative order-2 hidden w-[5px] shrink-0 cursor-col-resize touch-none lg:block"
          >
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-line transition-colors group-hover:bg-accent group-active:bg-accent" />
          </div>
          <div className="relative order-3 flex min-h-0 min-w-0 flex-1 flex-col border-t border-line lg:border-t-0">
            {/* Compile problems live HERE, in the preview pane — quiet
                during generation (partial code fails routinely), visible
                with a retry once the dust settles. */}
            {compileError && !isCompiling && !isLoading && !isSvg && (
              <div className="absolute left-1/2 top-2 z-20 flex max-w-[420px] -translate-x-1/2 items-center gap-2 rounded-lg border border-warning/40 bg-surface px-3 py-1.5 text-xs text-warning shadow-md">
                <span className="min-w-0 flex-1 truncate" title={compileError}>
                  Preview compile failed: {compileError}
                </span>
                <button
                  onClick={() => void compileAndPreview(tsxCode)}
                  className="shrink-0 font-medium text-accent hover:underline"
                >
                  Retry
                </button>
              </div>
            )}
            <div className="flex min-h-[280px] min-w-0 flex-1 overflow-hidden p-3">
              {isSvg ? (
                <SvgPreview code={substitutedSvg} />
              ) : moduleUrl ? (
                <RemotionPreview
                  fill
                  toolbarZoomOnly
                  toolbarPortal={previewToolbarEl}
                  playbackPortal={playbackBarEl}
                  moduleUrl={moduleUrl}
                  inputProps={fieldValues}
                  durationFrames={resolveDurationFrames(fieldValues, typeof fieldValues.fps === "number" && fieldValues.fps > 0 ? fieldValues.fps : canvasFps, 150, fieldSchema)}
                  fps={
                    typeof fieldValues.fps === "number" && fieldValues.fps > 0
                      ? fieldValues.fps
                      : canvasFps
                  }
                  width={previewCanvasWidth}
                  height={previewCanvasHeight}
                  baseWidth={previewCanvasWidth}
                  baseHeight={previewCanvasHeight}
                />
              ) : isCompiling ? (
                <div className="m-auto flex items-center gap-3">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
                  <span className="text-sm text-ink-2">
                    Compiling component for preview...
                  </span>
                </div>
              ) : tsxCode ? (
                <div className="m-auto flex w-full flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-warning" />
                    <span className="text-xs font-medium text-warning">
                      Preview compilation failed -- view code below
                    </span>
                  </div>
                  <pre className="max-h-48 overflow-auto rounded-lg bg-code p-3 font-mono text-xs text-ink-2">
                    <code>{tsxCode.slice(0, 500)}</code>
                  </pre>
                </div>
              ) : (
                <div className="m-auto py-12 text-center text-sm text-ink-3">
                  <p>Preview will appear here</p>
                  <p className="mt-1 text-xs text-ink-3">
                    Generate a template to see a live preview
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Tabs panel — the left pane. Build tab houses the chat + prompt
              input and is the default; Fields and Code tabs only light up
              once there's a generated result. */}
          {true && (
            <div className="order-1 flex min-h-0 min-w-0 flex-1 flex-col lg:w-[var(--split)] lg:flex-none">
              {/* Fields scrolls within the pane; Code hands the full pane
                  height to Monaco, which scrolls internally. */}
              {(
                <div
                  className={
                    shownTab === "code"
                      ? "flex min-h-0 flex-1 flex-col p-3"
                      : "min-h-0 flex-1 overflow-y-auto p-3"
                  }
                >
                  {shownTab === "context" && (
                    <ContextPanel
                      entries={displayedContext}
                      onChange={handleContextChange}
                    />
                  )}
                  {shownTab === "fields" && (() => {
                    const content = (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 rounded-lg bg-surface-3 p-0.5">
                            <button
                              type="button"
                              onClick={() => setFieldsMode("values")}
                              className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                                fieldsMode === "values"
                                  ? "bg-surface text-ink"
                                  : "text-ink-2 hover:text-ink"
                              }`}
                            >
                              Values
                            </button>
                            <button
                              type="button"
                              onClick={() => setFieldsMode("schema")}
                              className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                                fieldsMode === "schema"
                                  ? "bg-surface text-ink"
                                  : "text-ink-2 hover:text-ink"
                              }`}
                            >
                              Edit Schema
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFieldsFullscreen((v) => !v)}
                            className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                          >
                            {fieldsFullscreen ? "Exit" : "Fullscreen"}
                          </button>
                        </div>
                        {fieldsMode === "values" ? (
                          fieldSchema.length > 0 ? (
                            <FieldEditor
                              fields={fieldSchema}
                              values={fieldValues}
                              onChange={handleFieldChange}
                            />
                          ) : (
                            <div className="py-8 text-center text-sm text-ink-3">
                              No fields. Switch to "Edit Schema" to add some.
                            </div>
                          )
                        ) : (
                          <FieldSchemaEditor
                            fields={fieldSchema}
                            projectKind={projectKind}
                            onChange={(updated) => {
                              const oldKeys = new Set(fieldSchema.map((f) => f.key));
                              const newKeys = new Set(updated.map((f) => f.key));
                              // SVG: the user places `{{key}}` tokens in the
                              // source by hand — the TSX-oriented code-sync
                              // (Props interface + destructured params) would
                              // corrupt the SVG, so only seed default values.
                              if (isSvg) {
                                for (const f of updated) {
                                  if (!oldKeys.has(f.key)) {
                                    setFieldValues((prev) => ({ ...prev, [f.key]: f.default }));
                                  }
                                }
                                setFieldSchema(updated);
                                return;
                              }
                              // Remotion: detect added/removed fields and sync code
                              let code = tsxCode;
                              // Insert new fields into code
                              for (const f of updated) {
                                if (!oldKeys.has(f.key)) {
                                  code = insertFieldIntoCode(code, f.key, f.type);
                                  setFieldValues((prev) => ({ ...prev, [f.key]: f.default }));
                                }
                              }
                              // Remove deleted fields from code
                              for (const f of fieldSchema) {
                                if (!newKeys.has(f.key)) {
                                  code = removeFieldFromCode(code, f.key);
                                }
                              }
                              if (code !== tsxCode) setTsxCode(code);
                              setFieldSchema(updated);
                            }}
                          />
                        )}
                      </div>
                    );

                    if (fieldsFullscreen) {
                      return (
                        <div className="fixed inset-0 z-50 flex flex-col bg-surface">
                          <div className="flex-1 overflow-y-auto p-4">
                            {content}
                          </div>
                          <div className="border-t border-line px-4 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => setFieldsFullscreen(false)}
                              className="text-sm text-ink-2 hover:text-ink"
                            >
                              Press Esc or click to exit fullscreen
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return content;
                  })()}
                  {shownTab === "code" && (
                    <CodeEditor
                      value={tsxCode}
                      onChange={setTsxCode}
                      language={isSvg ? "xml" : "tsx"}
                      isFullscreen={codeFullscreen}
                      onToggleFullscreen={() => setCodeFullscreen((v) => !v)}
                      // SVG has no field schema — no Insert Field affordance.
                      onInsertField={
                        isSvg
                          ? undefined
                          : (field, updatedCode) => {
                              setFieldSchema((prev) => [...prev, field]);
                              setTsxCode(updatedCode);
                              setFieldValues((prev) => ({
                                ...prev,
                                [field.key]: field.default,
                              }));
                            }
                      }
                    />
                  )}
                  {shownTab === "settings" && (
                    <div className="flex flex-col gap-8">
                      {isSvg ? (
                        <SvgSettingsPanel
                          svg={substitutedSvg}
                          fileBase={svgFileBase}
                        />
                      ) : (
                        <>
                          {/* (a) Canvas — the composition natives for THIS
                              project. No template row exists, so these live in
                              workspace state + the editor draft and drive the
                              preview live; Save-as-template captures them. */}
                          <section className="flex flex-col gap-3">
                            <h3 className="text-sm font-semibold text-ink">
                              Canvas
                            </h3>
                            <p className="text-[11px] text-ink-3">
                              The composition's native size and frame rate for this
                              project. Changing these updates the live preview and
                              is captured when you Save as template.
                            </p>
                            <CanvasFields
                              width={canvasWidth}
                              height={canvasHeight}
                              fps={canvasFps}
                              onWidth={setCanvasWidth}
                              onHeight={setCanvasHeight}
                              onFps={setCanvasFps}
                            />
                          </section>
                          {/* (b) Preview appearance — BG + content-scale,
                              relocated from the preview toolbar (now zoom-only).
                              Same localStorage slots as TemplateDetail. */}
                          <PreviewAppearanceSection />
                          {/* No render-output section — the workspace has no
                              render pipeline. */}
                        </>
                      )}
                      {/* Brand style — generations follow the team's style
                          profile by default; this project can opt out or
                          substitute its own for off-brand work. */}
                      <section className="flex flex-col gap-3">
                        <h3 className="text-sm font-semibold text-ink">
                          Brand style
                        </h3>
                        <p className="text-[11px] text-ink-3">
                          Generations follow your team's style profile
                          (Settings → Brand style) by default. Override it here
                          for this project only.
                        </p>
                        <select
                          value={
                            styleMode === "client"
                              ? `client:${styleClientId ?? ""}`
                              : styleMode
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v.startsWith("client:")) {
                              setStyleMode("client");
                              setStyleClientId(v.slice("client:".length));
                            } else {
                              setStyleMode(v as "brand" | "off" | "custom");
                              setStyleClientId(null);
                            }
                          }}
                          className="max-w-xs rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-line-strong focus:outline-none"
                        >
                          <option value="brand">
                            Team brand style (default)
                          </option>
                          {(styleClients ?? []).map((c) => (
                            <option key={c.id} value={`client:${c.id}`}>
                              Client: {c.name}
                            </option>
                          ))}
                          <option value="off">No style profile</option>
                          <option value="custom">
                            Custom for this project…
                          </option>
                        </select>
                        {styleMode === "custom" && (
                          <textarea
                            value={styleOverride}
                            onChange={(e) => setStyleOverride(e.target.value)}
                            rows={4}
                            maxLength={2000}
                            placeholder="e.g. 1980s VHS glitch aesthetic, heavy grain, slow dreamy pacing, muted earth tones"
                            className="max-w-md rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-line-strong focus:outline-none"
                          />
                        )}
                      </section>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Assistant slide-over — the conversation lives here. Opens via
          "View output" on the working pill or the Output button; follow-up
          prompts can be sent from its composer too. */}
      <AssistantDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <ConversationHistory
          messages={messages}
          isGenerating={wsIsGenerating}
          isConnected={isConnected}
          error={error}
          isIterating={!!activeEntry}
          onSubmitPrompt={(text) => void handleGenerate(text)}
          onInterrupt={sendInterrupt}
          onCancel={sendCancel}
          transcriptRestoredFromCache={transcriptRestoredFromCache}
          isLoadingJsonl={isLoadingJsonl}
          onLoadJsonl={loadJsonl}
          sessionInputTokens={sessionUsage.inputTokens}
          sessionOutputTokens={sessionUsage.outputTokens}
          sessionCostUsd={sessionUsage.costUsd}
          initialDraft={initialDraft}
          model={genModel}
          onModelChange={setGenModel}
          genProvider={genProvider}
          onProviderChange={setGenProvider}
          isSvgSession={isSvg}
          currentLifecycle={currentLifecycle}
          currentActivity={currentActivity}
          generationStartedAt={generationStartedAt}
          liveOutputTokens={liveOutputTokens}
          referenceAssets={referenceAssets}
          referenceUploading={refUploading}
          referenceError={refError}
          maxReferences={MAX_REFERENCES}
          onAttachReferences={(files) => void attachReferenceFiles(files)}
          onRemoveReference={removeReference}
        />
      </AssistantDrawer>

      {/* Bottom strip — the playback bar spans the whole work area (both
          panes). RemotionPreview portals the transport controls (incl. the
          player-fullscreen button) into the slot; kept as thin as possible.
          SVG sessions have no player, so the strip is omitted entirely. */}
      {!isSvg && (
        <div className="flex min-h-[30px] shrink-0 items-center border-t border-line bg-surface-2 px-2 py-0.5">
          <div
            ref={setPlaybackBarEl}
            className="flex min-w-0 flex-1 items-center"
          />
        </div>
      )}

    </div>
  );
}
